// todo: use Intl instead to format dates and currency
// dimport DateTimeFormat = Intl.DateTimeFormat;
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
// https://marcoscaceres.github.io/jsi18n/

import { format, isValid, parse } from 'date-fns'
import { de, enGB, es, pl, ru, uk, zhCN } from 'date-fns/locale'

export enum DatePart {
    Week = 'w',
    Day = 'd',
    Hour = 'h',
    Minute = 'm',
    Second = 's'
}

export enum DateFormatType {
    dateOnly,
    timeOnly,
    minAndSec,
    dateAndTime,
    dateAndDayOfWeek
}

const locales = {
    ru: ru,
    en: enGB,
    de: de,
    pl: pl,
    uk: uk,
    es: es,
    // 'cn' is the app's code for Chinese
    cn: zhCN
}

export const formatDate = (
    date: Date | string | number,
    formatType: DateFormatType = DateFormatType.dateOnly,
    dateFormat = 'dd.MM.yyyy',
    language = 'ru'
) => {
    if (date === null || date === undefined || date === '') {
        return ''
    }

    const value = new Date(date)

    if (!isValid(value)) {
        return ''
    }

    const locale = locales[language]

    switch (formatType) {
        case DateFormatType.dateOnly:
            return format(value, dateFormat)
        case DateFormatType.timeOnly:
            return format(value, 'HH:mm')
        case DateFormatType.minAndSec:
            return format(value, 'mm:ss')
        case DateFormatType.dateAndTime:
            return format(value, `${dateFormat} HH:mm`)
        case DateFormatType.dateAndDayOfWeek:
            return format(value, `eeee, ${dateFormat}`, { locale: locale })
        default:
            return ''
    }
}

/**
 * Parses a date typed by the user in the cabinet's date format (dd.MM.yyyy, MM.dd.yy, yyyy-MM-dd).
 * Without it Fluent's DatePicker falls back to Date.parse, which reads 01.09.2026 as January 9.
 * Accepts 1-digit day/month, a 2-digit year and an optional time; returns null when the text is not a date.
 */
export const parseDateByFormat = (
    value: string,
    dateFormat = 'dd.MM.yyyy',
    referenceDate = new Date()
): Date | null => {
    const text = (value || '').trim()

    if (!text) {
        return null
    }

    // the cabinet format may use either yyyy or yy; accept both year lengths
    const fullYearFormat = dateFormat.replace(/y+/, 'yyyy')
    const shortYearFormat = dateFormat.replace(/y+/, 'yy')
    const dateFormats = [dateFormat, fullYearFormat, shortYearFormat].filter(
        (item, index, all) => all.indexOf(item) === index
    )
    const candidates = [...dateFormats, ...dateFormats.map((item) => `${item} HH:mm`)]

    for (const candidate of candidates) {
        const date = parse(text, candidate, referenceDate)

        if (isValid(date) && date.getFullYear() >= 1000) {
            return date
        }
    }

    return null
}

export const formatTime = (time: string, format: string = 'hh:mm:ss'): string => {
    // 00:00:15.6089124
    const timeRegex = /(\d{2}):(\d{2}):(\d{2}).\d.*/g

    if (!timeRegex.test(time)) return ''

    const hours = parseInt(time.replace(timeRegex, '$1'), 10),
        minutes = parseInt(time.replace(timeRegex, '$2'), 10),
        seconds = parseInt(time.replace(timeRegex, '$3'), 10)

    const h = hours < 10 ? '0' + hours : hours.toString(),
        m = minutes < 10 ? '0' + minutes : minutes.toString(),
        s = seconds < 10 ? '0' + seconds : seconds.toString()

    let res = format
    res = res.replace('hh', h)
    res = res.replace('mm', m)
    res = res.replace('ss', s)

    return res
}

export const prepareDateFromString = (value: string, date: Date = new Date()): Date => {
    const values = (value || '').trim().split('.')
    const day = values.length > 0 ? Math.max(1, Math.min(31, parseInt(values[0], 10))) : date.getDate()
    const month = values.length > 1 ? Math.max(1, Math.min(12, parseInt(values[1], 10))) - 1 : date.getMonth()
    let year = values.length > 2 ? parseInt(values[2], 10) : date.getFullYear()
    if (year < 100) {
        year += date.getFullYear() - (date.getFullYear() % 100)
    }
    return new Date(year, month, day)
}

export const parseDate = (dateValue: string | number | Date): Date => {
    if (dateValue === null || dateValue === undefined || dateValue === '') {
        return null
    }

    if (dateValue instanceof Date) {
        return dateValue
    }

    if (typeof dateValue === 'number') {
        return new Date(dateValue)
    }

    // example 2019-03-01T06:26:12.9775876Z
    const DATE_PATTERN_UTC = /\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[1-2]\d|3[0-1])T(?:[0-1]\d|2[0-3]):[0-5]\d:[0-5]\d(.\d*)?Z/

    if (DATE_PATTERN_UTC.test(dateValue)) {
        return new Date(dateValue)
    }

    // [dd.MM.yyyy] hh:mm
    const dateRegex = /(\d{1,2}).(\d{1,2}).(\d{4})/g

    if (!dateRegex.test(dateValue)) return null

    const days = parseInt(dateValue.replace(dateRegex, '$1'), 10),
        month = parseInt(dateValue.replace(dateRegex, '$2'), 10),
        year = parseInt(dateValue.replace(dateRegex, '$3'), 10)

    const date = new Date(year, month - 1, days)

    // dd.MM.yyyy [hh:mm]
    const timeRegex = /\d+.\d+.\d+ (\d{1,2}):(\d{1,2})/g
    if (timeRegex.test(dateValue)) {
        const h = parseInt(dateValue.replace(timeRegex, '$1'), 10),
            m = parseInt(dateValue.replace(timeRegex, '$2'), 10)
        date.setHours(h)
        date.setMinutes(m)
    }

    return date
}

export const roundPercentage = (value: number): number => {
    const decimals = 2
    const d = Math.pow(10, decimals)
    return Math.round(value * d) / d
}

export const roundNormTime = (value: number): number => {
    const decimals = 6
    const d = Math.pow(10, decimals)
    return Math.round(value * d) / d
}

export const roundCurrency = (value: number, decimals: number = 4): number => {
    /*
    Не правильно округляет, если например значение 0.0149 = 0.2, что не верно
    const valueTmp = Number(formatCurrency(value, decimals + 1))
    function round(valueTmp, decimals) {
        const num1: any = valueTmp + 'e' + decimals
        const num2: any = 'e-' + decimals
        return Math.round(num1) + num2
    }

    return Number(round(valueTmp, decimals))*/

    return parseFloat(formatCurrency(value, decimals))
}

export const roundCurrency2 = (value: number, decimals: number = 4): number => {
    /*
     Тест пройдет до 2
    let x = 0
    while (x < 2) {
        console.log(x + `${x} = ${roundCurrency2(x, 2)}`)
        x = x + 0.00001
    }
     }*/
    return parseFloat(formatCurrency(value, decimals))
}

// export const roundCurrency = (value: number, decimals: number = 4): number => {
//     const result = parseFloat(formatCurrency(value, decimals+1)).toFixed(decimals)
//     return parseFloat(result)
// }

/*
Функции для калькуляторов. Надо найти лучше место потом
 */
export const getPrecision = (number: number): number => {
    let e = 1
    while (Math.round(number * e) / e !== number) {
        e *= 10
    }
    return roundCurrency(Math.log(e) / Math.LN10, 1)
}

export const getVatPriceFromNoVat = (priceNoVat: number, vat: number, round: number): number => {
    return roundCurrency((priceNoVat * vat) / 100, round)
}

export const getReverseValueByPercent = (price: number, percent: number, precision: number = 2): number => {
    if (percent !== -100) {
        return roundCurrency2((price / (100 + percent)) * percent, precision)
    }
    return 0
}

export const getPercentByValue = (priceWthDiscount: number, priceWoDiscount: number, precision: number = 2): number => {
    if (priceWoDiscount !== 0) {
        return roundCurrency2((priceWthDiscount / priceWoDiscount - 1) * 100, precision)
    }
    return 0
}

export const getPriceForOne = (priceForMany: number, count: number, precision: number): number => {
    if (count !== 0) {
        return roundCurrency2(priceForMany / count, precision)
    }
    return 0
}

export const getPriceForMany = (priceForOne: number, count: number, precision: number): number => {
    return roundCurrency2(priceForOne * count, precision)
}

export const getPriceWithExtraPercent1 = (price: number, extraPercent: number, round: number): number => {
    return roundCurrency2((price * (100 + extraPercent)) / 100, round)
}

export const getPriceWithExtraPercent2 = (price: number, extraPercent: number, round: number): number => {
    return roundCurrency2(price + (price * extraPercent) / 100, round)
}

export const formatCurrency = (value: number, decimals: number = 2): string => {
    if (value === undefined || value === null) {
        return ''
    }
    const d = Math.pow(10, decimals)
    const rounded = Math.round(value * d) / d

    return rounded.toFixed(decimals)
}

// «Надбавка / Скидка»: the stored amount is signed (surcharge > 0, discount < 0); an explicit «+» keeps a surcharge
// from reading as a discount
export const formatSignedAmount = (value: number, decimals: number = 2): string => {
    const formatted = formatCurrency(value, decimals)

    // compare the rounded value: 0.001 must read «0.00», not «+0.00»
    return Number(formatted) > 0 ? `+${formatted}` : formatted
}

/**
 * «Дата оплаты», «Срок оплаты», «Дата исполнения» of an order are stored as the station's wall-clock time labelled UTC
 * («2026-10-05T23:30:00Z» means 23:30 at the station). Read the stored date and time as local ones, like the order
 * form does (getLocalDate); otherwise in the evening the day shows as the next one
 */
export const parseWallClockDate = (value: Date | string | null | undefined): Date | null => {
    if (value === null || value === undefined || value === '') {
        return null
    }

    if (value instanceof Date) {
        return isValid(value)
            ? new Date(
                  value.getUTCFullYear(),
                  value.getUTCMonth(),
                  value.getUTCDate(),
                  value.getUTCHours(),
                  value.getUTCMinutes(),
                  value.getUTCSeconds()
              )
            : null
    }

    const parts = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value)

    if (!parts) {
        return null
    }

    const [, year, month, day, hours = '0', minutes = '0', seconds = '0'] = parts

    return new Date(+year, +month - 1, +day, +hours, +minutes, +seconds)
}

export const formatTotals = (value: number, decimals: number = 3): string => {
    if (value === undefined || value === null) {
        return ''
    }
    const d = Math.pow(10, decimals)
    const rounded = Math.round(value * d) / d

    return rounded.toFixed(decimals)
}

export const formatOrderNum = (num: number | string, emptyText: string): string => {
    return Boolean(num) ? `№${num}` : emptyText
}

export const toOffsetString = (date: Date): string => {
    const copiedDate = new Date(date.getTime())
    const hoursDiff = copiedDate.getHours() - copiedDate.getTimezoneOffset() / 60
    const minutesDiff = (copiedDate.getMinutes() - copiedDate.getTimezoneOffset()) % 60
    copiedDate.setHours(hoursDiff)
    copiedDate.setMinutes(minutesDiff)
    return copiedDate.toISOString()
}

export const dateDiff = (datePart: DatePart, from: Date, to: Date): number => {
    const diff = to.getTime() - from.getTime()
    const divideBy = {
        [DatePart.Week]: 604800000,
        [DatePart.Day]: 86400000,
        [DatePart.Hour]: 3600000,
        [DatePart.Minute]: 60000,
        [DatePart.Second]: 1000
    }
    return Math.floor(diff / divideBy[datePart])
}

export const getReplacedCommaByDotStr = (str: string) => str.replace(/,/g, '.')

export const toLocalTimeHourAndMinuteString = (date: string | Date) =>
    new Date(date).toLocaleTimeString('ru', {
        hour: 'numeric',
        minute: 'numeric'
    })

export const trimStringByLength = (str, length: number) => {
    return str?.length > length ? unescape(str.substring(0, length)) + '...' : str
}
